import { TrackedArray } from 'tracked-built-ins';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import type { PropertySelection } from 'uplisting-frontend/components/ui/property/selector';

type Property = ModelRegistry['property'];
type MultiUnit = ModelRegistry['multi-unit'];

function sameIds(left: { id: string }[], right: { id: string }[]): boolean {
  if (left.length !== right.length) {
    return false;
  }

  const ids = new Set(right.map((item) => item.id));

  return left.every((item) => ids.has(item.id));
}

function unitsWithImplied(
  properties: Property[],
  multiUnits: MultiUnit[],
): MultiUnit[] {
  const implied = properties.flatMap((property) => property.multiUnits.slice());

  return [...new Set([...multiUnits, ...implied])];
}

export class PropertySelectionDraft implements PropertySelection {
  properties: Property[];
  multiUnits: MultiUnit[];

  readonly showMultiUnits: boolean;

  constructor(
    properties: Property[] = [],
    multiUnits: MultiUnit[] = [],
    showMultiUnits = true,
  ) {
    this.showMultiUnits = showMultiUnits;
    this.properties = new TrackedArray(properties);
    this.multiUnits = new TrackedArray(
      showMultiUnits ? unitsWithImplied(properties, multiUnits) : multiUnits,
    );
  }

  static from(
    record: PropertySelection,
    showMultiUnits = true,
  ): PropertySelectionDraft {
    return new PropertySelectionDraft(
      record.properties.slice(),
      record.multiUnits.slice(),
      showMultiUnits,
    );
  }

  get isEmpty(): boolean {
    return !this.properties.length && !this.multiUnits.length;
  }

  hasChangesFrom(record: PropertySelection): boolean {
    const saved = PropertySelectionDraft.from(record, this.showMultiUnits);

    return (
      !sameIds(this.properties, saved.properties) ||
      !sameIds(this.multiUnits, saved.multiUnits)
    );
  }

  unitsToSave(): MultiUnit[] {
    if (this.showMultiUnits) {
      return this.multiUnits.slice();
    }

    return this.multiUnits.filter((multiUnit) =>
      this.properties.includes(multiUnit.property),
    );
  }
}
